/* Eyewear Essentials - shared motion layer (ee-fx)
 * Reveals, card tilt, gallery scroll-spy and a sticky buy bar. Applies site-wide.
 *
 * Ownership: the homepage section (ee-studio-home) ships its own motion, so every
 * feature here skips anything inside [data-ee-home]. Opt any other block out with
 * [data-ee-no-fx].
 *
 * Reveal safety: the hiding class (.eefx-r) is only ever added by this script,
 * so with JS off nothing is hidden. Reveal uses a self-terminating scroll sweep
 * rather than IntersectionObserver, because an observer misses elements that are
 * jumped over (hash links, restored scroll position, back navigation) and leaves
 * them permanently invisible.
 */
(function () {
  'use strict';

  var mq = function (q) { return window.matchMedia ? window.matchMedia(q) : { matches: false }; };
  var RM = function () { return mq('(prefers-reduced-motion: reduce)').matches; };
  var FINE = function () { return mq('(hover: hover) and (pointer: fine)').matches; };
  var HAS_IO = 'IntersectionObserver' in window;

  var REVEAL_SELECTORS = [
    '.ee-section > h1',
    '.ee-section > .ee-eyebrow',
    '.ee-shape-browse',
    '.ee-catalog-bar',
    '.ee-filters',
    '.ee-product-grid > *',
    '.ee-gallery-block',
    '.ee-product-info',
    '.ee-approved-film',
    '.ee-product-description .ee-prose > *',
    '.ee-prose > h2',
    '.ee-prose > p',
    '.ee-prose > ul',
    '.ee-empty',
    '.ee-pagination'
  ].join(',');

  // Anything the homepage section already animates, or anything explicitly opted out.
  function owned(el) {
    return !!(el.closest('[data-ee-home]') || el.closest('[data-ee-no-fx]'));
  }

  var pending = [];
  var sweepFrame = 0;
  var sweepBound = false;

  function sweep() {
    sweepFrame = 0;
    if (!pending.length) return;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    var edge = vh * 0.94;
    var still = [];
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      var r = el.getBoundingClientRect();
      // Reveal anything that has reached the viewport OR already scrolled past it.
      if (r.top < edge) { el.classList.add('eefx-in'); } else { still.push(el); }
    }
    pending = still;
    if (!pending.length && sweepBound) {
      window.removeEventListener('scroll', queueSweep);
      window.removeEventListener('resize', queueSweep);
      sweepBound = false;
    }
  }

  function queueSweep() {
    if (!sweepFrame) sweepFrame = requestAnimationFrame(sweep);
  }

  function reveal(scope) {
    if (RM()) return;
    var nodes;
    try {
      nodes = Array.prototype.slice.call(scope.querySelectorAll(REVEAL_SELECTORS));
    } catch (e) { return; }
    nodes = nodes.filter(function (el) {
      return !owned(el) && !el.classList.contains('eefx-r');
    });
    if (!nodes.length) return;

    var counts = new Map();
    nodes.forEach(function (el) {
      var parent = el.parentElement;
      var i = counts.get(parent) || 0;
      counts.set(parent, i + 1);
      if (i > 0) el.style.setProperty('--eefx-d', Math.min(i * 70, 420) + 'ms');
      el.classList.add('eefx-r');
    });

    pending = pending.concat(nodes);

    if (!sweepBound) {
      window.addEventListener('scroll', queueSweep, { passive: true });
      window.addEventListener('resize', queueSweep, { passive: true });
      sweepBound = true;
    }
    sweep();

    // Belt and braces: never leave content invisible.
    setTimeout(function () { if (pending.length) sweep(); }, 2500);
  }

  function tilt(scope) {
    if (RM() || !FINE()) return;
    scope.querySelectorAll('.ee-product-grid > *').forEach(function (card) {
      if (card.dataset.eefxTilt || owned(card)) return;
      card.dataset.eefxTilt = '1';
      var frame = 0, rx = 0, ry = 0, active = false;
      var apply = function () {
        frame = 0;
        card.style.transform = active
          ? 'perspective(900px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg) translateZ(8px)'
          : '';
      };
      var queue = function () { if (!frame) frame = requestAnimationFrame(apply); };
      card.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        var r = card.getBoundingClientRect();
        if (!r.width || !r.height) return;
        ry = ((e.clientX - r.left) / r.width - 0.5) * 8;
        rx = (0.5 - (e.clientY - r.top) / r.height) * 6;
        active = true;
        queue();
      });
      card.addEventListener('pointerleave', function () { active = false; rx = ry = 0; queue(); });
    });
  }

  function gallery(scope) {
    var gal = scope.querySelector('.ee-gallery');
    var thumbs = scope.querySelector('.ee-gallery-thumbs');
    if (!gal || !thumbs || thumbs.dataset.eefxReady || owned(thumbs)) return;
    thumbs.dataset.eefxReady = '1';

    var links = Array.prototype.slice.call(thumbs.querySelectorAll('a[href^="#Media-"]'));
    var shots = Array.prototype.slice.call(gal.querySelectorAll('.ee-gallery-image[id]'));
    if (!links.length || !shots.length) return;

    thumbs.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#Media-"]');
      if (!a) return;
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: RM() ? 'auto' : 'smooth', block: 'center' });
      try { history.replaceState(null, '', a.getAttribute('href')); } catch (err) {}
    });

    if (!HAS_IO) return;
    var spy = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var hash = '#' + en.target.id;
        links.forEach(function (l) {
          l.classList.toggle('eefx-thumb-active', l.getAttribute('href') === hash);
        });
      });
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
    shots.forEach(function (s) { spy.observe(s); });
  }

  function buybar() {
    if (!HAS_IO) return;
    if (document.querySelector('.eefx-buybar')) return;
    var info = document.querySelector('.ee-product-info');
    if (!info || owned(info)) return;
    var addBtn = info.querySelector('.ee-add-button');
    if (!addBtn) return;

    var titleEl = document.querySelector('.ee-product-page h1') || info.querySelector('h2');
    var priceEl = info.querySelector('[data-price]');
    var shot = document.querySelector('.ee-gallery-image img');

    var bar = document.createElement('div');
    bar.className = 'eefx-buybar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', (titleEl ? titleEl.textContent.trim() : 'Product') + ' - quick add');

    if (shot) {
      var img = document.createElement('img');
      img.className = 'eefx-buybar-img';
      img.src = shot.currentSrc || shot.src;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      bar.appendChild(img);
    }

    var txt = document.createElement('div');
    txt.className = 'eefx-buybar-txt';
    var t = document.createElement('span');
    t.className = 'eefx-buybar-title';
    t.textContent = titleEl ? titleEl.textContent.trim() : '';
    var p = document.createElement('span');
    p.className = 'eefx-buybar-price';
    p.textContent = priceEl ? priceEl.textContent.trim() : '';
    txt.appendChild(t);
    txt.appendChild(p);
    bar.appendChild(txt);

    var btn = document.createElement('button');
    btn.type = 'button';
    var syncBtn = function () {
      btn.textContent = (addBtn.textContent || '').replace(/↗/g, '').trim() || 'Add';
      btn.disabled = !!addBtn.disabled;
      if (priceEl) p.textContent = priceEl.textContent.trim();
    };
    syncBtn();
    btn.addEventListener('click', function () { addBtn.click(); });
    bar.appendChild(btn);

    document.body.appendChild(bar);

    try {
      var mo = new MutationObserver(syncBtn);
      mo.observe(addBtn, { attributes: true, childList: true, characterData: true, subtree: true });
      if (priceEl) mo.observe(priceEl, { childList: true, characterData: true, subtree: true });
    } catch (e) {}

    var io = new IntersectionObserver(function (entries) {
      var en = entries[0];
      var passed = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('eefx-in', passed);
    }, { threshold: 0 });
    io.observe(addBtn);
  }

  function init(scope) {
    var root = scope || document;
    var main = root.querySelector ? (root.querySelector('main') || root) : root;
    try { reveal(main); } catch (e) {}
    try { tilt(main); } catch (e) {}
    try { gallery(main); } catch (e) {}
    try { buybar(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  window.addEventListener('pageshow', queueSweep);
})();
